<script setup lang="ts">
import { propsFactory } from "@soft-db/soft-utils";
import type { Numeric } from "d3";
import { computed, ref, useSlots, type PropType } from "vue";
import type {
	CartesianChartOptions,
	CartesianSerieWithValue,
	LegendOptions,
	LineChartProps,
	LinePathOptions,
} from "../interfaces";
import { useManualZoom } from "../use/manual-zoom";
import CartesianChart from "./CartesianChart.vue";
import LinePath from "./LinePath.vue";

const props = defineProps(
	propsFactory<LineChartProps>()({
		options: {
			type: Object as PropType<CartesianChartOptions>,
			required: true,
		},
		series: {
			type: Array as PropType<LinePathOptions[]>,
			default: () => [],
		},
		legend: Object as PropType<LegendOptions>,
		step: Boolean,
	})
);

const emit = defineEmits<{
	(e: "hover", id: string, hover: boolean): void;
	(e: "click:line", payload: { x: Numeric; y: Numeric }): void;
	(e: "click", payload: Partial<Record<string, Numeric>>): void;
	(e: "dblclick", payload: Partial<Record<string, Numeric>>): void;
	(e: "update:activeSerie", payload: string): void;
}>();

const selectedSerie = ref("");

const activeSerie = computed(() => {
	if (!selectedSerie.value) return;

	return props.series.find((serie) => serie.id === selectedSerie.value);
});

const inactiveSeries = computed(() => {
	return props.series.filter((serie) => serie.id !== selectedSerie.value);
});

const lineHover = (id: string, hover: boolean) => {
	emit("hover", id, hover);
};

const chartRef = ref<InstanceType<typeof CartesianChart>>();

const { setZoom } = useManualZoom(
	chartRef,
	computed(() => props.options)
);
defineExpose({ setZoom });

defineSlots<{
	"legend:item:label"(props: { serie: CartesianSerieWithValue }): void;
	"legend:item:value"(props: { serie: CartesianSerieWithValue }): void;
}>();

const slots = useSlots();
const slotsWithoutDefault = computed(() => {
	const s = { ...slots };
	delete s.default;
	return s;
});
</script>

<template>
	<CartesianChart
		ref="chartRef"
		v-bind="{ ...options }"
		v-model:activeSerie="selectedSerie"
		:series="series"
		:legend="legend"
		@click="(e) => emit('click', e)"
		@dblclick="(e) => emit('dblclick', e)"
		@click:outside="selectedSerie = ''"
	>
		<template v-for="(_, name) in slotsWithoutDefault" #[name]="slotData">
			<slot :name="name" v-bind="slotData" />
		</template>
		<g>
			<LinePath
				v-for="serie of inactiveSeries"
				:key="`line-${serie.id}`"
				v-bind="serie"
				:curveStyle="serie.style"
				:step="step"
				:disabled="!!selectedSerie"
				:live="options.live"
				:animation="options.animation"
				@hover="(hover) => lineHover(serie.id, hover)"
				@click="(position) => $emit('click:line', position)"
			/>
			<LinePath
				v-if="activeSerie"
				v-bind="activeSerie"
				:curveStyle="activeSerie.style"
				active
				:step="step"
				@hover="(hover) => activeSerie && lineHover(activeSerie.id, hover)"
				@click="(position) => $emit('click:line', position)"
			/>
		</g>
	</CartesianChart>
</template>
